import { useMemo } from "react";
import { Customer, Finding, ReportSummary, ScanResult } from "../types/customer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Send, FileText, AlertTriangle, AlertCircle, Info, Shield, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ReportModalProps {
  customer: Customer | null;
  open: boolean;
  onClose: () => void;
  onOpenDetails?: () => void;
  pdfAvailable?: boolean;
  pdfGenerating?: boolean;
  onOpenPdf?: () => void;
  onRemakePdf?: () => void;
  findings: Finding[];
  scanResults: ScanResult[];
}

function SeverityBadge({ severity }: { severity: Finding["severity"] }) {
  const config = {
    critical: { variant: "danger" as const, icon: AlertTriangle },
    high: { variant: "danger" as const, icon: AlertCircle },
    medium: { variant: "warning" as const, icon: AlertTriangle },
    low: { variant: "info" as const, icon: Info },
    info: { variant: "neutral" as const, icon: Info },
  };

  const { variant, icon: Icon } = config[severity];

  return (
    <Badge variant={variant} className="flex items-center gap-1">
      <Icon className="h-3 w-3" />
      {severity.toUpperCase()}
    </Badge>
  );
}

function formatScannerLabel(scanner: ScanResult["scanner"]) {
  if (scanner === "sitecheck") return "Site Checks";
  if (scanner === "httpx") return "httpx";
  if (scanner === "wpscan") return "WPScan";
  return "Nuclei";
}

export function ReportModal({
  customer,
  open,
  onClose,
  onOpenDetails,
  pdfAvailable = false,
  pdfGenerating = false,
  onOpenPdf,
  onRemakePdf,
  findings,
  scanResults,
}: ReportModalProps) {
  const { toast } = useToast();

  const sortedFindings = useMemo(() => {
    const rank = (severity: Finding["severity"]) => {
      if (severity === "info") return 0;
      if (severity === "low") return 1;
      if (severity === "medium") return 2;
      if (severity === "high") return 3;
      if (severity === "critical") return 4;
      return 5;
    };
    return findings
      .slice()
      .sort(
        (a, b) =>
          rank(a.severity) - rank(b.severity) ||
          String(a.scanner || "").localeCompare(String(b.scanner || "")) ||
          String(a.title || "").localeCompare(String(b.title || "")),
      );
  }, [findings]);

  const scannersUsed = Array.from(new Set(scanResults.map((r) => r.scanner))).map(formatScannerLabel);
  const scanTimestamp = scanResults.length > 0 ? scanResults[0].timestamp : customer?.lastScan;

  const summary: ReportSummary = {
    totalFindings: findings.length,
    critical: findings.filter((f) => f.severity === "critical").length,
    high: findings.filter((f) => f.severity === "high").length,
    medium: findings.filter((f) => f.severity === "medium").length,
    low: findings.filter((f) => f.severity === "low").length,
    info: findings.filter((f) => f.severity === "info").length,
  };

  const handleSendToClient = () => {
    toast({
      title: "Coming Soon",
      description: "Email delivery feature will be available soon.",
    });
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-card border-border max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex flex-row items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/20 p-2">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold text-foreground">
                Security Report
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                {customer.name} - {customer.url}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {typeof onOpenDetails === "function" && (
              <Button size="sm" variant="secondary" onClick={onOpenDetails}>
                <FileText className="mr-1 h-4 w-4" />
                Open
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={onOpenPdf} disabled={!pdfAvailable}>
              <Download className="mr-1 h-4 w-4" />
              Open PDF
            </Button>
            <Button size="sm" variant="secondary" onClick={onRemakePdf} disabled={pdfGenerating || !customer.lastScan}>
              {pdfGenerating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}
              {pdfGenerating ? "Remaking..." : "Remake PDF"}
            </Button>
            <Button size="sm" variant="secondary" onClick={handleSendToClient}>
              <Send className="mr-1 h-4 w-4" />
              Send to Client
            </Button>
            {/* DialogContent already provides a top-right close button */}
          </div>
        </DialogHeader>

        <div className="mt-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Executive Summary</h3>
          <div className="grid grid-cols-6 gap-3">
            <div className="rounded-lg border border-border bg-muted/20 p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{summary.totalFindings}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
            <div className="rounded-lg border border-[hsl(var(--ss-danger)/0.4)] bg-[hsl(var(--ss-danger)/0.12)] p-4 text-center">
              <p className="text-2xl font-bold text-[hsl(var(--ss-danger))]">{summary.critical}</p>
              <p className="text-sm text-[hsl(var(--ss-danger))]">Critical</p>
            </div>
            <div className="rounded-lg border border-[hsl(var(--ss-danger)/0.3)] bg-[hsl(var(--ss-danger)/0.08)] p-4 text-center">
              <p className="text-2xl font-bold text-[hsl(var(--ss-danger))]">{summary.high}</p>
              <p className="text-sm text-[hsl(var(--ss-danger))]">High</p>
            </div>
            <div className="rounded-lg border border-[hsl(var(--ss-warning)/0.4)] bg-[hsl(var(--ss-warning)/0.12)] p-4 text-center">
              <p className="text-2xl font-bold text-[hsl(var(--ss-warning))]">{summary.medium}</p>
              <p className="text-sm text-[hsl(var(--ss-warning))]">Medium</p>
            </div>
            <div className="rounded-lg border border-[hsl(var(--ss-info)/0.4)] bg-[hsl(var(--ss-info)/0.12)] p-4 text-center">
              <p className="text-2xl font-bold text-[hsl(var(--ss-info))]">{summary.low}</p>
              <p className="text-sm text-[hsl(var(--ss-info))]">Low</p>
            </div>
            <div className="rounded-lg border border-muted bg-muted/20 p-4 text-center">
              <p className="text-2xl font-bold text-muted-foreground">{summary.info}</p>
              <p className="text-sm text-muted-foreground">Info</p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex min-h-0 flex-1 flex-col">
          <h3 className="text-lg font-semibold text-foreground mb-4">Findings</h3>
          <div className="space-y-3 min-h-0 flex-1 overflow-y-auto pr-1">
            {sortedFindings.length === 0 && (
              <div className="rounded-lg border border-border bg-muted/10 p-4">
                <p className="text-sm text-muted-foreground">
                  No findings yet. Run a scan to populate results.
                </p>
              </div>
            )}
            {sortedFindings.map((finding) => (
              <div
                key={finding.id}
                className="rounded-lg border border-border bg-muted/10 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <SeverityBadge severity={finding.severity} />
                    <div>
                      <h4 className="font-medium text-foreground">{finding.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {finding.description}
                      </p>
                      {finding.reference && (
                        <p className="text-xs text-primary mt-2">
                          Reference: {finding.reference}
                        </p>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {finding.scanner}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 rounded-lg border border-border bg-muted/20 p-4 shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-primary" />
            <h4 className="font-medium text-foreground">Scan Information</h4>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Last Scan:</span>{" "}
              <span className="text-foreground">{scanTimestamp || "N/A"}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Scanners Used:</span>{" "}
              <span className="text-foreground">{scannersUsed.join(", ") || "N/A"}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Target URL:</span>{" "}
              <span className="text-primary">{customer.url}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Report Generated:</span>{" "}
              <span className="text-foreground">{new Date().toLocaleString()}</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
